/**
 * Maps existing MarkovLab cached GET payloads onto Workspace view-model
 * contracts. Presentation components must not parse raw backend shapes.
 *
 * recommended_strategy / recommendation.reason are never used as eligibility,
 * suitability, recommendation, approval, or screen-state inputs.
 */
import { dteBand, earningsConflict, POLICY_WINDOW } from "@/workspace/mockTickerData";
import {
  BASKET, DATA_LAYER, EVENT_LAYER, SCREEN, actionLayerOf, dataLayerOf, eventLayerOf,
  reasonForScreen, screenFromLayers,
} from "@/workspace/screenLanguage";
import { findRankedStrategy } from "@/workspace/handoff";
import {
  COMPLETED_MULTILEG_PRICING_METHODS,
  CONTEXT_KIND,
  CURRENT_QUOTE_AVAILABILITY,
  CURRENT_QUOTE_PRECEDENCE,
  FIELD_STATUS,
  HISTORICAL_CONTEXT_KIND,
  RESEARCH_FOCUS_KIND,
  WORKSPACE_FIELD_CATALOGUE,
  liveField,
} from "./workspaceLiveTypes";
import { LIVE_LABEL, LIVE_UNAVAILABLE } from "./workspaceLiveUnavailable";

const EMPTY = LIVE_UNAVAILABLE.notInCache || "Not available from current cached data";

export function sessionDate(value) {
  if (value == null) return null;
  const s = String(value);
  const m = s.match(/(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

function present(value) {
  return value !== undefined && value !== null && value !== "";
}

function num(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function unitConfidence(value) {
  const n = num(value);
  if (n == null) return null;
  return n > 1 ? n / 100 : n;
}

function pickPresent(...values) {
  for (const v of values) {
    if (present(v)) return v;
  }
  return null;
}

function earningsQuality(status) {
  const s = String(status || "").toLowerCase();
  if (s === "confirmed") return "confirmed";
  if (s === "estimated" || s === "expected") return "estimated";
  return "unavailable";
}

function pricingMethodLabel(raw) {
  if (!present(raw)) return null;
  return String(raw);
}

/** Date-only YYYY-MM-DD (including lastTradeDate) is not a verified option quote time. */
export function isParseableOptionQuoteTime(value) {
  if (!present(value)) return false;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  return /T\d{2}:\d{2}/.test(s) || /\d{2}:\d{2}:\d{2}/.test(s);
}

function quoteAsOf(fresh) {
  return fresh?.option_quote_as_of || fresh?.as_of || null;
}

function optionsBasis(fresh, wing) {
  const ts = String(fresh?.quote_timestamp_quality || "").toLowerCase();
  const method = String(fresh?.pricing_method || wing?.pricing_method || "").toLowerCase();
  const errs = Array.isArray(wing?.validation_errors) ? wing.validation_errors : [];
  if (!fresh && !wing) return "unavailable";
  if (String(fresh?.status || "").toLowerCase() === "unavailable") return "unavailable";
  if (ts !== "verified") return "unverified";
  if (!isParseableOptionQuoteTime(quoteAsOf(fresh))) return "unverified";
  if (errs.includes("missing_premium") || errs.includes("missing_bid_ask")) return "partial";
  if (method.includes("model") || method.includes("black") || method.includes("bs")) return "modelled";
  return "observed";
}

function hasOptionChainPayload(data, wing) {
  const fresh = data?.option_freshness;
  const hasFresh = Boolean(fresh && typeof fresh === "object" && Object.keys(fresh).length > 0);
  const hasWing = Boolean(wing);
  const expiries = data?.available_expirations;
  const hasExp = Array.isArray(expiries) && expiries.length > 0;
  return hasFresh || hasWing || hasExp;
}

function timestampIsVerified(fresh) {
  const ts = String(fresh?.quote_timestamp_quality || "").toLowerCase();
  if (ts !== "verified") return false;
  return isParseableOptionQuoteTime(quoteAsOf(fresh));
}

function hasSelectedStructure(wing) {
  return Boolean(wing && (wing.legs.length || wing.strategyId));
}

function freshnessBlocksSelection(data) {
  const ready = data?.option_readiness || {};
  const fresh = data?.option_freshness || {};
  return ready.blocks_selection === true
    || ready.ready === false
    || fresh.blocks_selection === true;
}

function completedMultilegPremium(fresh, wing) {
  if (!hasSelectedStructure(wing) || wing.premium == null) return false;
  const errs = wing.validationErrors || [];
  if (errs.includes("missing_premium") || errs.includes("missing_bid_ask")) return false;
  const method = String(fresh?.pricing_method || wing.pricingMethod || "").toLowerCase();
  return COMPLETED_MULTILEG_PRICING_METHODS.includes(method);
}

export function classifyCurrentQuoteAvailability({ data, wing }) {
  if (!hasOptionChainPayload(data, wing)) return CURRENT_QUOTE_AVAILABILITY.no_chain;
  if (!timestampIsVerified(data?.option_freshness || {})) {
    return CURRENT_QUOTE_AVAILABILITY.unverified_timestamp;
  }
  if (freshnessBlocksSelection(data)) return CURRENT_QUOTE_AVAILABILITY.freshness_blocked;
  if (!hasSelectedStructure(wing)) return CURRENT_QUOTE_AVAILABILITY.no_selected_structure;
  if (!completedMultilegPremium(data?.option_freshness, wing)) {
    return CURRENT_QUOTE_AVAILABILITY.premium_unavailable;
  }
  return CURRENT_QUOTE_AVAILABILITY.verified_observed;
}

export function classifyHistoricalContextKind({ families, scalars }) {
  const rows = Array.isArray(families) ? families : [];
  const hasFamilies = rows.some((f) => Array.isArray(f.names) && f.names.length);
  const s = scalars || {};
  const hasScalars = s.sample_size != null || s.win_rate != null || s.avg_return_on_risk != null;
  if (hasFamilies || hasScalars) return HISTORICAL_CONTEXT_KIND.historical_backtest_not_current;
  return HISTORICAL_CONTEXT_KIND.historical_unavailable;
}

export function currentQuoteCopy(availability) {
  if (availability === CURRENT_QUOTE_AVAILABILITY.unverified_timestamp) {
    return LIVE_UNAVAILABLE.currentPricedUnverified;
  }
  if (availability === CURRENT_QUOTE_AVAILABILITY.no_chain) {
    return LIVE_UNAVAILABLE.currentPricedNoChain;
  }
  if (availability === CURRENT_QUOTE_AVAILABILITY.freshness_blocked) {
    return LIVE_UNAVAILABLE.currentPricedFreshnessBlocked;
  }
  if (availability === CURRENT_QUOTE_AVAILABILITY.no_selected_structure) {
    return LIVE_UNAVAILABLE.currentPricedNoStructure;
  }
  if (availability === CURRENT_QUOTE_AVAILABILITY.premium_unavailable) {
    return LIVE_UNAVAILABLE.currentPricedPremiumUnavailable;
  }
  return LIVE_UNAVAILABLE.currentPricedNoTable;
}

/** Per-row quote state for the Options strategy table.
 *
 * Two axes decide a row: broker entitlement and quote validity. No
 * broker-entitled options snapshot source exists in the frontend yet, so no
 * row may resolve to `delayed` or `realtime` today. Those keys exist so the
 * table can accept a validated snapshot later without a layout change.
 */
export const ROW_QUOTE_STATE = {
  no_broker: "no_broker",
  broker_no_quote: "broker_no_quote",
  quote_unverified: "quote_unverified",
  delayed: "delayed",
  realtime: "realtime",
  historical_only: "historical_only",
  data_unavailable: "data_unavailable",
};

export function rowQuoteStateFor(availability) {
  if (!availability) return ROW_QUOTE_STATE.data_unavailable;
  if (availability === CURRENT_QUOTE_AVAILABILITY.no_chain) return ROW_QUOTE_STATE.historical_only;
  if (availability === CURRENT_QUOTE_AVAILABILITY.unverified_timestamp) return ROW_QUOTE_STATE.quote_unverified;
  return ROW_QUOTE_STATE.no_broker;
}

export function windowRibbonAvailable(series) {
  return Array.isArray(series)
    && series.length > 0
    && series.every((r) => r.regimeState != null || r.confirmedRegimeState != null);
}

function alignRegime(ohlcv, history) {
  const rows = Array.isArray(history) ? history : [];
  const byDate = new Map();
  rows.forEach((row) => {
    const d = sessionDate(row?.date);
    if (d) byDate.set(d, row);
  });
  const historyDates = [...byDate.keys()].sort();
  const stats = {
    ohlcvCount: ohlcv.length,
    historyCount: rows.length,
    matchedCount: 0,
    firstOhlcv: ohlcv[0]?.date || null,
    lastOhlcv: ohlcv[ohlcv.length - 1]?.date || null,
    firstHistory: historyDates[0] || null,
    lastHistory: historyDates[historyDates.length - 1] || null,
    missingSample: [],
  };
  if (!ohlcv.length || !rows.length) {
    return { ok: false, bars: ohlcv, message: LIVE_UNAVAILABLE.regimeAlignment, stats };
  }
  const bars = ohlcv.map((bar) => {
    const d = sessionDate(bar.date);
    const hit = d ? byDate.get(d) : null;
    if (!hit) {
      if (stats.missingSample.length < 8) stats.missingSample.push(d);
      return {
        ...bar,
        regimeState: null,
        confirmedRegimeState: null,
        regimeConfidence: null,
      };
    }
    stats.matchedCount += 1;
    return {
      ...bar,
      regimeState: hit.state_label || null,
      confirmedRegimeState: hit.state_5d || null,
      regimeConfidence: unitConfidence(hit.confidence ?? hit.dominant_prob),
    };
  });
  const ok = stats.matchedCount === ohlcv.length;
  return {
    ok,
    bars,
    message: ok ? null : LIVE_UNAVAILABLE.regimeAlignment,
    stats,
  };
}

function mapOhlcv(raw) {
  return (Array.isArray(raw) ? raw : []).map((b) => {
    const date = sessionDate(b.date);
    return {
      date,
      open: num(b.open),
      high: num(b.high),
      low: num(b.low),
      close: num(b.close),
      volume: num(b.volume),
      sma20: num(b.sma20 ?? b.sma_20),
      sma50: num(b.sma50 ?? b.sma_50),
      sma200: num(b.sma200 ?? b.sma_200),
      rsi14: num(b.rsi14 ?? b.rsi_14),
      macdHistogram: num(b.macdHistogram ?? b.macd_histogram),
      bbUpper: num(b.bbUpper ?? b.bb_upper),
      bbLower: num(b.bbLower ?? b.bb_lower),
      ema9: num(b.ema9 ?? b.ema_9),
      ema20: num(b.ema20 ?? b.ema_20),
      atr14: num(b.atr14 ?? b.atr_14),
      trendState: b.trendState || b.trend || null,
    };
  }).filter((b) => b.date && b.close != null && b.high != null && b.low != null);
}

function mapExpirations(raw) {
  return (Array.isArray(raw) ? raw : []).map((e) => {
    const date = sessionDate(e.date || e.expiry);
    const dte = num(e.dte);
    if (!date || dte == null) return null;
    return {
      date,
      dte,
      monthly: Boolean(e.monthly),
      friday: Boolean(e.friday),
      status: e.status || null,
      band: dteBand(dte),
      label: `${date} · ${dte} DTE`,
    };
  }).filter(Boolean);
}

function mapKeyLevels(raw) {
  const support = (raw?.support || []).map((s) => ({
    label: s.label || "Support",
    level: num(s.level),
  })).filter((s) => s.level != null);
  const resistance = (raw?.resistance || []).map((s) => ({
    label: s.label || "Resistance",
    level: num(s.level),
  })).filter((s) => s.level != null);
  return { support, resistance };
}

function mapWings(wing) {
  if (!wing || typeof wing !== "object") return null;
  const legs = Array.isArray(wing.legs)
    ? wing.legs.map((l) => ({
      side: l.side || l.action || "",
      strike: num(l.strike),
      right: l.right || l.type || "",
      note: l.note || "",
      bid: num(l.bid),
      ask: num(l.ask),
      last: num(l.last ?? l.last_price),
    })).filter((l) => l.strike != null)
    : [];
  const premium = num(wing.net_premium ?? wing.premium ?? wing.credit);
  return {
    strategyId: wing.strategy || null,
    valid: wing.valid !== false,
    legs,
    premium,
    pricingMethod: pricingMethodLabel(wing.pricing_method),
    userMessage: wing.user_message || null,
    validationErrors: Array.isArray(wing.validation_errors) ? wing.validation_errors : [],
  };
}

function familyContext(ranked) {
  const rows = Array.isArray(ranked?.ranked_strategies) ? ranked.ranked_strategies : [];
  const seen = new Map();
  rows.forEach((r) => {
    const family = r.family || "unspecified";
    if (!seen.has(family)) seen.set(family, []);
    const name = r.strategy_name || r.strategy_id;
    if (name && !seen.get(family).includes(name)) seen.get(family).push(name);
  });
  return [...seen.entries()].map(([family, names]) => ({
    family,
    names,
    displayFamily: formatHistoricalStrategyLabel(family),
    displayNames: names.map((n) => formatHistoricalStrategyLabel(n)).filter(Boolean),
  }));
}

const KNOWN_STRATEGY_LABELS = {
  covered_call: "Covered call",
  cash_secured_put: "Cash-secured put",
  bull_put_spread: "Bull put spread",
  iron_condor: "Iron condor",
};

function normalizeLabelKey(raw) {
  return String(raw || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function titleFromSnake(raw) {
  const parts = String(raw).trim().toLowerCase().replace(/-/g, "_").split("_").filter(Boolean);
  if (!parts.length) return "";
  if (parts[0] === "cash" && parts[1] === "secured") {
    return ["Cash-secured", ...parts.slice(2)].join(" ");
  }
  return parts.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");
}

/** Display-only. Does not invent a family or change payload identity. */
export function formatHistoricalStrategyLabel(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  const key = normalizeLabelKey(s);
  if (KNOWN_STRATEGY_LABELS[key]) return KNOWN_STRATEGY_LABELS[key];
  if (key === "premium_selling") return "Premium-Selling";
  if (key === "long_vol") return "Long-Vol";
  if (key === "equity") return "Equity";
  if (/[_-]/.test(s)) return titleFromSnake(s);
  if (/[a-z][A-Z]/.test(s) || /^[A-Z][a-z]+[A-Z]/.test(s)) {
    const spaced = s.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
    return titleFromSnake(spaced.replace(/\s+/g, "_"));
  }
  if (/^[a-z0-9]+$/.test(s)) return s.charAt(0).toUpperCase() + s.slice(1);
  return s;
}

export function formatHistoricalScalarDisplay(value) {
  if (value == null || value === "") return null;
  return value;
}

export function formatCanonicalFamilyLabel(family) {
  return formatHistoricalStrategyLabel(family);
}

export function researchRowsFromRanked(ranked, quoteState = ROW_QUOTE_STATE.data_unavailable) {
  const rows = Array.isArray(ranked?.ranked_strategies) ? ranked.ranked_strategies : [];
  return rows
    .filter((row) => row && row.strategy_id)
    .map((row) => ({
      strategy_id: row.strategy_id,
      strategy_name: row.strategy_name || null,
      family: row.family || null,
      risk_profile: Object.prototype.hasOwnProperty.call(row, "risk_profile") ? row.risk_profile : null,
      displayName: formatHistoricalStrategyLabel(row.strategy_name) || formatHistoricalStrategyLabel(row.strategy_id),
      familyDisplay: formatCanonicalFamilyLabel(row.family),
      // Workflow row identity only. Never a current contract or trade.
      quoteState,
      legs: null,
      strike: null,
      width: null,
      premium: null,
      weekly: null,
    }));
}

function emptyResearchMetrics() {
  return {
    sample_size: null,
    win_rate: null,
    avg_return_on_risk: null,
    assignment_rate: null,
    assignmentSupported: false,
    max_loss: null,
    profit_factor: null,
  };
}

function researchMetricsFromRow(row, regimeRows) {
  const metrics = emptyResearchMetrics();
  if (!row) return metrics;
  metrics.sample_size = present(row.bt_sample_size) ? num(row.bt_sample_size) : null;
  metrics.win_rate = present(row.bt_win_rate) ? num(row.bt_win_rate) : null;
  metrics.profit_factor = present(row.bt_profit_factor) ? num(row.bt_profit_factor) : null;
  if (present(row.avg_return_on_risk)) {
    metrics.avg_return_on_risk = num(row.avg_return_on_risk);
  }
  const current = Array.isArray(regimeRows) ? regimeRows.find((r) => r.current) : null;
  if (current) {
    if (metrics.avg_return_on_risk == null && present(current.avg_return_on_risk)) {
      metrics.avg_return_on_risk = current.avg_return_on_risk;
    }
    if (present(current.max_loss)) metrics.max_loss = current.max_loss;
    if (current.assignmentSupported) {
      metrics.assignmentSupported = true;
      metrics.assignment_rate = current.assignment_rate;
    }
  }
  return metrics;
}

export function buildResearchFocus({
  ranked,
  rankedStatus,
  requestedStrategy,
  backtestCtx,
  confirmedState,
} = {}) {
  const requestedId = requestedStrategy == null || requestedStrategy === ""
    ? null
    : String(requestedStrategy).trim() || null;
  const regimeTable = backtestCtx ? mapRegimeTable(backtestCtx, confirmedState) : [];
  const base = {
    kind: RESEARCH_FOCUS_KIND.choose,
    requestedId,
    strategyId: null,
    displayName: null,
    family: null,
    familyDisplay: null,
    riskProfile: null,
    historicalLabel: LIVE_UNAVAILABLE.historicalBadge,
    simulationLabel: LIVE_UNAVAILABLE.researchSimulationLabel,
    methodology: LIVE_UNAVAILABLE.researchMethodology,
    metrics: emptyResearchMetrics(),
    regimeTable,
    usedWingFallback: false,
    usedRankedZeroFallback: false,
  };
  if (!requestedId) return base;
  if (rankedStatus === "idle" || rankedStatus === "loading") {
    return { ...base, kind: RESEARCH_FOCUS_KIND.pending };
  }
  const row = findRankedStrategy(requestedId, ranked);
  if (!row) {
    return { ...base, kind: RESEARCH_FOCUS_KIND.unavailable };
  }
  const metrics = researchMetricsFromRow(row, regimeTable);
  const limited = row.bt_insufficient === true || metrics.sample_size == null || metrics.sample_size < 20;
  return {
    ...base,
    kind: limited ? RESEARCH_FOCUS_KIND.limited : RESEARCH_FOCUS_KIND.ready,
    strategyId: row.strategy_id,
    displayName: formatHistoricalStrategyLabel(row.strategy_name) || formatHistoricalStrategyLabel(row.strategy_id),
    family: row.family || null,
    familyDisplay: formatCanonicalFamilyLabel(row.family),
    riskProfile: Object.prototype.hasOwnProperty.call(row, "risk_profile") ? row.risk_profile : null,
    metrics,
    regimeTable,
  };
}

function mapRegimeTable(ctx, confirmedState) {
  const regimes = ctx?.regimes || {};
  const keys = ["Quiet Range", "Bullish Trend", "Bearish Trend", "Volatile"];
  return keys.map((key) => {
    const src = regimes[key] || {};
    const n = num(src.sample_size);
    const insufficient = src.insufficient === true || n == null || n < 20;
    return {
      key,
      label: key === "Quiet Range" ? "Quiet" : key === "Bullish Trend" ? "Bullish" : key === "Bearish Trend" ? "Bearish" : "Volatile",
      current: key === confirmedState,
      sample_size: n,
      insufficient,
      win_rate: present(src.win_rate) ? num(src.win_rate) : null,
      assignment_rate: present(src.assign_rate) ? num(src.assign_rate) : null,
      assignmentSupported: present(src.assign_rate),
      avg_return_on_risk: present(src.avg_return_on_risk) ? num(src.avg_return_on_risk) : null,
      max_loss: present(src.max_loss) ? num(src.max_loss) : null,
      mean_pnl: present(src.mean_pnl) ? num(src.mean_pnl) : null,
    };
  });
}

function mapTalk(doc) {
  const items = Array.isArray(doc?.items) ? doc.items : [];
  return items.map((it, i) => ({
    id: `${it.ticker || doc?.ticker || "talk"}-${i}`,
    date: sessionDate(it.date) || sessionDate(doc?.updated_at) || "",
    headline: it.headline || "",
    source: it.kind || it.source || "cached headline",
    summary: it.summary || "",
    topics: Array.isArray(it.topics) ? it.topics : [],
    tone: it.tone || null,
    url: it.url || it.article_url || it.link || null,
  }));
}

function insiderSide(tx) {
  const code = String(tx.transaction_code || "").toUpperCase();
  const ad = String(tx.acquired_or_disposed || "").toUpperCase();
  if (code === "P" || ad === "A") return "Buy";
  if (code === "S" || ad === "D") return "Sell";
  return tx.type || "Exchange";
}

function ptrSide(type) {
  const t = String(type || "");
  if (/purchase|buy|acquired/i.test(t)) return "Buy";
  if (/sale|sell|disposed/i.test(t)) return "Sell";
  return "Exchange";
}

function ptrChamber(raw) {
  const s = String(raw || "");
  if (/senate/i.test(s)) return "Senate";
  if (/house|representative/i.test(s)) return "House";
  return s || "—";
}

function mapShadow(doc) {
  const ticker = doc?.ticker || "";
  const holders = (doc?.institutions?.top_holders || []).map((h) => ({
    investorName: h.investor_name || "—",
    positionValueUsd: num(h.market_value) || 0,
    shares: num(h.shares) || 0,
    newPositionUsd: 0,
    exitUsd: 0,
    periodOfReport: doc?.institutions?.period_of_report || null,
  }));
  const insider = (doc?.insider?.latest || []).map((tx, i) => {
    const shares = num(tx.shares) || 0;
    const price = num(tx.price) || 0;
    return {
      id: `${ticker}-f4-${tx.date || i}-${i}`,
      date: sessionDate(tx.date) || "",
      valueUsd: shares && price ? shares * price : shares,
      shares,
      type: insiderSide(tx),
      insiderName: tx.insider_name || "—",
    };
  });
  const congress = (doc?.congress || []).map((c, i) => ({
    id: `${ticker}-ptr-${c.date || i}-${i}`,
    politicianName: c.person || "—",
    party: "",
    chamber: ptrChamber(c.chamber),
    type: ptrSide(c.type),
    amountBracket: c.amount || "—",
    amountMidpointUsd: null,
    tradeDate: sessionDate(c.date) || "",
    delayFlag: false,
    delayDays: 0,
  }));
  const proposedSales = (doc?.proposed_sales || []).map((s, i) => ({
    id: `${ticker}-144-${s.date || i}-${i}`,
    date: sessionDate(s.date) || "",
    seller: s.seller || "—",
    shares: num(s.shares) || 0,
    marketValueUsd: num(s.market_value) || 0,
  }));
  return {
    holders,
    insider,
    congress,
    proposedSales,
    buyCount30d: num(doc?.insider?.buy_count_30d) || 0,
    sellCount30d: num(doc?.insider?.sell_count_30d) || 0,
    institutionsNote: doc?.institutions?.note || "13F is a lagged holdings snapshot, not a buy or sell tape.",
    form144Note: "Form 144 notices are proposed sales, not confirmed sales.",
    congressNote: "Congressional amounts are disclosed as brackets, not exact dollars.",
    updatedAt: doc?.updated_at || null,
  };
}

function defaultExpiry(expiries) {
  return (expiries.find((e) => e.band === "target" && e.monthly)
    || expiries.find((e) => e.band === "target")
    || expiries[0]
    || null);
}

function buildNotes({ earnings, optionsState, disagreement, basis, ready }) {
  const notes = [];
  if (disagreement) {
    notes.push({ level: "caution", text: "Instant and confirmed HMM states differ." });
  }
  if (basis === "unverified") {
    notes.push({ level: "caution", text: "Option quote timestamp is unavailable." });
  } else if (basis === "partial") {
    notes.push({ level: "caution", text: "Required quote fields are unavailable." });
  } else if (basis === "modelled") {
    notes.push({ level: "info", text: "Indicative estimate only." });
  } else if (basis === "observed") {
    notes.push({ level: "info", text: "Observed pricing available." });
  } else {
    notes.push({ level: "caution", text: "Pricing unavailable." });
  }
  if (earnings?.past) {
    notes.push({
      level: "info",
      text: `Most recent earnings date ${earnings.date}. ${LIVE_LABEL.earningsPast}.`,
    });
  } else if (earnings?.quality === "confirmed") {
    notes.push({ level: "info", text: `Confirmed earnings date ${earnings.date}.` });
  } else if (earnings?.quality === "estimated") {
    notes.push({ level: "caution", text: `Estimated earnings date ${earnings.date || "unavailable"}.` });
  } else {
    notes.push({ level: "caution", text: "Earnings date unavailable." });
  }
  notes.push({
    level: ready === true ? "info" : "caution",
    text: ready === true ? BASKET.available : BASKET.unavailable,
  });
  return notes;
}

function addField(fields, name, value, status, extras) {
  fields[name] = liveField(value, status, extras);
}

export function adaptWorkspaceLive({
  symbol,
  tickerData,
  history,
  historyStatus,
  ranked,
  rankedStatus,
  requestedStrategy,
  backtestCtx,
  talk,
  shadow,
  fetchedAt,
} = {}) {
  const fields = {};
  const data = tickerData || {};
  const fetched = fetchedAt || null;
  const ohlcv = mapOhlcv(data.ohlcv);
  const lastBar = ohlcv[ohlcv.length - 1];
  const prevBar = ohlcv[ohlcv.length - 2];
  const price = num(data.current_price ?? data.quote?.price ?? lastBar?.close);
  let change = null;
  let changePct = null;
  let changeStatus = FIELD_STATUS.unavailable;
  if (lastBar && prevBar && lastBar.close != null && prevBar.close) {
    change = +(lastBar.close - prevBar.close).toFixed(2);
    changePct = +(((lastBar.close / prevBar.close) - 1) * 100).toFixed(2);
    changeStatus = FIELD_STATUS.derived;
  }
  const changeLabel = changeStatus === FIELD_STATUS.derived
    ? (prevBar?.date ? LIVE_LABEL.changeVsPriorSession : LIVE_LABEL.changeSincePrior)
    : null;
  const changeQualityLabel = changeStatus === FIELD_STATUS.derived ? LIVE_LABEL.changeQuality : null;

  const ro = data.regime_output || {};
  const hz = ro.horizons || {};
  const instant = pickPresent(hz.instant?.state, ro.dominant_state_label);
  const confirmed = pickPresent(hz.smoothed_5d?.state, ro.primary_state_label, instant);
  const confidence = unitConfidence(hz.instant?.confidence ?? ro.dominant_prob);
  const sojournDays = num(data.regime_output?.horizons?.smoothed_5d?.bars_held
    ?? data.layer_status?.regime_age_bars);
  const ageBars = num(hz.smoothed_5d?.bars_held);
  const sojournP75 = num(data.regime_p75);
  const disagreement = Boolean(
    hz.disagreement?.instant_vs_5d_differ
    || (instant && confirmed && instant !== confirmed),
  );

  const earn = data.earnings || {};
  const earnings = {
    date: sessionDate(earn.next_earnings_date),
    quality: earningsQuality(earn.next_earnings_status),
    daysTo: num(earn.days_to_earnings),
    past: false,
  };
  if (!earnings.date) earnings.quality = "unavailable";
  const sessionAsOf = lastBar?.date || sessionDate(data.chart_as_of || data.ohlcv_close?.bar_date);
  earnings.past = Boolean(
    (earnings.date && sessionAsOf && earnings.date < sessionAsOf)
    || (Number.isFinite(earnings.daysTo) && earnings.daysTo < 0),
  );

  const signals = data.signals || {};
  const technicals = data.technicals || {};
  const funds = data.fundamentals || {};
  const fresh = data.option_freshness || {};
  const readyObj = data.option_readiness || {};
  const wing = mapWings(data.wing_scenarios);
  const basis = optionsBasis(fresh, data.wing_scenarios);
  const currentAvailability = classifyCurrentQuoteAvailability({ data, wing });
  const allowCurrentPremium = false;
  const ready = readyObj.ready === true;
  const expiries = mapExpirations(data.available_expirations);
  const historyRows = history?.history || history || [];
  const aligned = (!history && historyStatus !== "ready" && historyStatus !== "error")
    ? { ok: false, bars: ohlcv, message: "Loading cached regime history…" }
    : alignRegime(ohlcv, historyRows);
  const hasPerBarSma50 = aligned.bars.some((b) => b.sma50 != null);
  const hasPerBarSma200 = aligned.bars.some((b) => b.sma200 != null);
  const hasTrendTape = aligned.bars.every((b) => present(b.trendState));
  const companyName = pickPresent(data.company, data.company_name, funds.company, funds.company_name, funds.fields?.company, funds.fields?.company_name);
  const industry = pickPresent(data.industry, funds.industry, funds.fields?.industry);
  const sector = pickPresent(funds.sector, data.sector);

  const techScore = num(signals.technical_score ?? technicals.technical_score ?? data.technical_score);
  const fundScore = num(funds.fundamental_score);

  addField(fields, "header.symbol", symbol || data.ticker, present(symbol || data.ticker) ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "ticker", fetchedAt: fetched });
  addField(fields, "header.last", price, price != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "current_price | quote.price | ohlcv[-1].close", asOf: data.quote?.as_of || data.ohlcv_close?.bar_date, fetchedAt: fetched });
  addField(fields, "header.changePct", changePct, changeStatus, {
    path: "ohlcv[-1].close vs prior close",
    fetchedAt: fetched,
    asOf: lastBar?.date || data.ohlcv_close?.bar_date || null,
    label: changeLabel || LIVE_LABEL.cached,
  });
  addField(fields, "header.sector", sector, sector ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "fundamentals.sector", fetchedAt: fetched });
  addField(fields, "header.company", companyName, companyName ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "company / fundamentals.company" });
  addField(fields, "header.industry", industry, industry ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "industry" });
  addField(fields, "header.technicalScore", techScore, techScore != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "signals.technical_score", asOf: data.technicals_as_of });
  addField(fields, "header.fundamentalScore", fundScore, fundScore != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "fundamentals.fundamental_score" });
  addField(fields, "regime.instant", instant, instant ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "regime_output.horizons.instant.state", asOf: data.hmm_result_as_of });
  addField(fields, "regime.confirmed", confirmed, confirmed ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "regime_output.horizons.smoothed_5d.state", asOf: data.hmm_result_as_of });
  addField(fields, "regime.confidence", confidence, confidence != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "regime_output.dominant_prob" });
  addField(fields, "regime.sojourn", sojournDays, sojournDays != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "horizons.smoothed_5d.bars_held" });
  addField(fields, "regime.p75", sojournP75, sojournP75 != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "regime_p75" });
  addField(fields, "regime.disagreement", disagreement, FIELD_STATUS.derived, { path: "instant vs confirmed" });
  addField(fields, "earnings.date", earnings.date, earnings.date ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, {
    path: "earnings.next_earnings_date",
    label: earnings.past ? LIVE_LABEL.earningsPast : (earnings.date ? LIVE_LABEL.cached : LIVE_LABEL.dataUnavailable),
  });
  addField(fields, "earnings.status", earnings.quality, FIELD_STATUS.cached, { path: "earnings.next_earnings_status" });
  addField(fields, "options.freshness", fresh.status || null, fresh.status ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "option_freshness.status", asOf: fresh.option_quote_as_of, fetchedAt: fresh.option_snapshot_fetched_at || fetched });
  addField(
    fields,
    "options.pricingMethod",
    null,
    FIELD_STATUS.unavailable,
    { path: "option_freshness.pricing_method", label: LIVE_UNAVAILABLE.pricingMethodUnavailable },
  );
  addField(fields, "options.readiness", readyObj.ready, readyObj.ready != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "option_readiness.ready" });
  addField(fields, "overview.ohlcv", ohlcv.length, ohlcv.length ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "ohlcv[]", asOf: data.chart_as_of || data.ohlcv_close?.bar_date });
  addField(fields, "overview.regimeRibbon", aligned.ok, aligned.ok ? FIELD_STATUS.derived : FIELD_STATUS.unavailable, { path: "regime history joined on exact date" });
  addField(fields, "overview.trendTape", null, FIELD_STATUS.unavailable, { path: "per-bar trend" });
  addField(fields, "recommended_strategy", data.recommendation?.strategy || null, FIELD_STATUS.not_evaluated, { path: "intentionally unused" });

  const sma50 = num(signals.sma_50 ?? technicals.sma_50);
  const sma200 = num(signals.sma_200 ?? technicals.sma_200);
  const rsi14 = num(signals.rsi_14 ?? technicals.rsi_14);
  const macdHist = num(signals.macd_histogram ?? technicals.macd_histogram);
  const adx = num(signals.adx_14 ?? technicals.adx_14);
  const bbUpper = num(signals.bb_upper ?? technicals.bb_upper);
  const bbLower = num(signals.bb_lower ?? technicals.bb_lower);
  addField(fields, "technicals.sma50", sma50, sma50 != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "signals.sma_50" });
  addField(fields, "technicals.sma200", sma200, sma200 != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "signals.sma_200" });
  addField(fields, "technicals.rsi", rsi14, rsi14 != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "signals.rsi_14" });
  addField(fields, "technicals.macd", macdHist, macdHist != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "signals.macd_histogram" });
  addField(fields, "technicals.adx", adx, adx != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "signals.adx_14" });
  addField(fields, "technicals.bb", bbUpper, bbUpper != null ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "signals.bb_upper / bb_lower" });
  addField(fields, "technicals.ema9", null, FIELD_STATUS.unavailable, { path: "—" });
  addField(fields, "technicals.ema20", null, FIELD_STATUS.unavailable, { path: "—" });
  addField(fields, "technicals.emaWeekly", null, FIELD_STATUS.unavailable, { path: "—" });
  addField(fields, "technicals.atr", null, FIELD_STATUS.unavailable, { path: "—" });
  addField(fields, "technicals.rs", null, FIELD_STATUS.unavailable, { path: "—" });

  const bt = data.backtest_validation || {};
  addField(fields, "options.backtest.sample_size", num(bt.sample_size), present(bt.sample_size) ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "backtest_validation.sample_size" });
  addField(fields, "options.backtest.win_rate", num(bt.win_rate), present(bt.win_rate) ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "backtest_validation.win_rate" });
  addField(fields, "options.backtest.avg_return_on_risk", num(bt.avg_return_on_risk), present(bt.avg_return_on_risk) ? FIELD_STATUS.cached : FIELD_STATUS.unavailable, { path: "backtest_validation.avg_return_on_risk" });

  const families = ranked ? familyContext(ranked) : [];
  const researchBrowser = researchRowsFromRanked(ranked, rowQuoteStateFor(currentAvailability));
  const researchFocus = buildResearchFocus({
    ranked,
    rankedStatus: rankedStatus || (ranked ? "ready" : "idle"),
    requestedStrategy,
    backtestCtx,
    confirmedState: confirmed,
  });
  const researchScalars = {
    sample_size: researchFocus.metrics.sample_size,
    win_rate: researchFocus.metrics.win_rate,
    avg_return_on_risk: researchFocus.metrics.avg_return_on_risk,
    assignment_rate: researchFocus.metrics.assignmentSupported ? researchFocus.metrics.assignment_rate : null,
  };
  const historicalKind = classifyHistoricalContextKind({
    families,
    scalars: researchFocus.kind === RESEARCH_FOCUS_KIND.ready || researchFocus.kind === RESEARCH_FOCUS_KIND.limited
      ? researchScalars
      : { sample_size: null, win_rate: null, avg_return_on_risk: null },
  });
  addField(fields, "options.strategyFamilyContext", families.length, families.length ? FIELD_STATUS.cached : (ranked ? FIELD_STATUS.unavailable : FIELD_STATUS.not_evaluated), { path: "ranked_strategies[].family / strategy_name" });
  addField(fields, "options.regimeBacktest", backtestCtx ? Boolean(backtestCtx.regimes) : null, backtestCtx?.regimes ? FIELD_STATUS.cached : (backtestCtx ? FIELD_STATUS.unavailable : FIELD_STATUS.not_evaluated), { path: "regime-backtest-context.regimes" });
  addField(fields, "talk.headlines", talk?.items?.length ?? null, talk?.items ? FIELD_STATUS.cached : (talk === undefined ? FIELD_STATUS.not_evaluated : FIELD_STATUS.unavailable), { path: "ticker-talk.items[]" });
  addField(fields, "ownership.shadow", shadow ? 1 : null, shadow ? FIELD_STATUS.cached : (shadow === undefined ? FIELD_STATUS.not_evaluated : FIELD_STATUS.unavailable), { path: "shadow-money" });

  const selectedExp = defaultExpiry(expiries);
  const conflict = earningsConflict(earnings, selectedExp?.dte);
  const dataLayer = dataLayerOf(basis);
  const eventLayer = eventLayerOf(conflict);
  const actionLayer = ready
    ? { key: "available", text: BASKET.available, tier: "info", mark: "●" }
    : actionLayerOf(dataLayer.key).key === "unavailable"
      ? actionLayerOf(dataLayer.key)
      : { key: "unavailable", text: BASKET.unavailable, tier: "blocking", mark: "!" };
  const screen = screenFromLayers(dataLayer.key, eventLayer.key);
  const ivRank = data.iv_rank_snapshot?.quality === "ready" ? num(data.iv_rank_snapshot?.value) : null;
  const premiumLabel = ivRank != null
    ? `IV rank ${ivRank} · cached data`
    : "IV context unavailable";

  const historicalContext = {
    kind: historicalKind,
    contextKind: CONTEXT_KIND.historical_backtest,
    families,
    scalars: researchScalars,
    regimeContext: researchFocus.regimeTable,
    label: "historical_backtest_not_current",
  };

  const currentQuote = {
    availability: currentAvailability,
    contextKind: CONTEXT_KIND.current_quote,
    premium: allowCurrentPremium ? wing?.premium ?? null : null,
    pricingMethod: null,
    selectedStructure: null,
    quoteTimestamp: timestampIsVerified(fresh) ? quoteAsOf(fresh) : null,
    quoteTimestampQuality: timestampIsVerified(fresh) ? "verified" : (fresh.quote_timestamp_quality || "unverified"),
    copy: currentQuoteCopy(currentAvailability),
    precedence: CURRENT_QUOTE_PRECEDENCE,
  };

  const liveRow = null;

  const talkItems = talk ? mapTalk(talk) : null;
  const ownership = shadow ? mapShadow(shadow) : null;

  const provenanceRows = Object.entries(fields).map(([field, f]) => ({
    field,
    surface: field.split(".")[0],
    source: "Existing cached GET",
    status: f.status,
    asOf: f.asOf,
    fetchedAt: f.fetchedAt,
    path: f.path,
  }));

  return {
    live: true,
    ticker: (symbol || data.ticker || "").toUpperCase(),
    companyName,
    industry,
    sector: sector || "—",
    price,
    change,
    changePct,
    changeLabel,
    changeQualityLabel,
    quoteQuality: currentAvailability === CURRENT_QUOTE_AVAILABILITY.verified_observed && basis === "observed"
      ? "cached"
      : (currentAvailability === CURRENT_QUOTE_AVAILABILITY.unverified_timestamp ? "unverified" : basis),
    quoteQualityLabel: currentAvailability === CURRENT_QUOTE_AVAILABILITY.unverified_timestamp
      ? "Option quote timestamp is unavailable"
      : basis === "observed"
        ? LIVE_LABEL.cached
        : basis === "partial"
          ? "Required quote fields are unavailable"
          : basis === "modelled"
            ? "indicative estimate only"
            : "data unavailable",
    regime: {
      state: confirmed || instant || "—",
      confidence: confidence ?? 0,
      confirmedState: confirmed || "—",
      instantState: instant || "—",
      disagreement,
      sojournDays: sojournDays ?? null,
      sojournP75: sojournP75 ?? null,
      ageBars: ageBars ?? sojournDays ?? null,
    },
    technicalScore: techScore,
    fundamentalScore: fundScore,
    earnings,
    optionsState: basis,
    decisionState: screen,
    decisionSummary: [
      confirmed ? `Confirmed HMM state ${confirmed}.` : "Confirmed HMM state unavailable.",
      instant && disagreement ? `Instant state ${instant}.` : null,
      dataLayer.text,
      eventLayer.text,
      actionLayer.text,
    ].filter(Boolean).join(" "),
    premium: { ivContext: ivRank != null ? "cached" : null, label: premiumLabel, expectedMovePct: null, expectedMoveNote: null },
    keyRisks: buildNotes({ earnings, optionsState: basis, disagreement, basis, ready }),
    blockers: ready ? [] : [{ level: "blocking", text: BASKET.unavailable }],
    series: aligned.bars,
    ribbonAvailable: aligned.ok,
    alignmentMessage: aligned.message,
    alignmentStats: aligned.stats,
    hasPerBarSma50,
    hasPerBarSma200,
    hasTrendTape,
    smaKeys: [
      hasPerBarSma50 ? "sma50" : null,
      hasPerBarSma200 ? "sma200" : null,
    ].filter(Boolean),
    signalsLive: {
      sma50,
      sma200,
      rsi14,
      macdHistogram: macdHist,
      adx14: adx,
      bbUpper,
      bbLower,
      trend: signals.trend || null,
      volume: num(signals.volume ?? lastBar?.volume),
    },
    keyLevels: mapKeyLevels(data.key_levels),
    availableExpirations: expiries,
    defaultExpiryDate: selectedExp?.date || null,
    optionStructure: liveRow,
    historicalContext,
    researchFocus,
    researchBrowser,
    researchRows: researchBrowser,
    rowQuoteState: rowQuoteStateFor(currentAvailability),
    currentQuote,
    currentQuoteAvailability: currentAvailability,
    historicalContextKind: historicalKind,
    layers: { data: dataLayer, event: eventLayer, action: actionLayer },
    screenDecision: screen,
    basketState: ready ? "available" : "unavailable",
    familyContext: families,
    regimeBacktest: backtestCtx || null,
    backtestScalars: {
      sample_size: present(bt.sample_size) ? num(bt.sample_size) : null,
      win_rate: present(bt.win_rate) ? num(bt.win_rate) : null,
      avg_return_on_risk: present(bt.avg_return_on_risk) ? num(bt.avg_return_on_risk) : null,
      profit_factor: present(bt.profit_factor) ? num(bt.profit_factor) : null,
      assignment_rate: null,
      data_quality: bt.data_quality || null,
    },
    wingDisplay: wing,
    talkItems,
    ownership,
    fundamentalsLive: {
      pe: num(funds.pe_ratio),
      forwardPe: num(funds.forward_pe),
      revenueGrowth: num(funds.revenue_growth),
      earningsGrowth: num(funds.earnings_growth),
      debtEquity: num(funds.debt_equity),
      analystCount: num(funds.analyst_count),
      targetMean: num(funds.target_mean_price),
      fieldQuality: funds.field_quality || {},
    },
    fields,
    provenanceRows,
    fetchedAt: fetched,
    asOf: {
      quote: data.quote?.as_of || null,
      ohlcv: data.chart_as_of || data.ohlcv_close?.bar_date || null,
      hmm: data.hmm_result_as_of || null,
      technicals: data.technicals_as_of || null,
      optionQuote: isParseableOptionQuoteTime(fresh.option_quote_as_of) ? fresh.option_quote_as_of : null,
      optionFetched: fresh.option_snapshot_fetched_at || null,
    },
    mappingReport: buildMappingReport(fields),
    policyWindow: POLICY_WINDOW,
    DATA_LAYER,
    EVENT_LAYER,
    SCREEN,
    liveReason: reasonForScreen(dataLayer.key, eventLayer.key),
  };
}

export function buildMappingReport(fields) {
  const live = Object.entries(fields).map(([field, f]) => ({
    field,
    endpoint: f.path?.includes("ticker-talk")
      ? "GET /api/ticker-talk/{ticker}"
      : f.path?.includes("shadow")
        ? "GET /api/shadow-money/{ticker}"
        : f.path?.includes("ranked")
          ? "GET /api/regime/ranked-strategies/{symbol}"
          : f.path?.includes("regime-backtest") || f.path?.includes("regimes")
            ? "GET /api/ticker/{symbol}/regime-backtest-context"
            : f.path?.includes("history")
              ? "GET /api/regime/{symbol}/history"
              : "GET /api/ticker/{symbol}/data",
    path: f.path,
    status: f.status,
    userLabel: f.label || statusLabel(f.status),
    asOf: f.asOf,
    fetchedAt: f.fetchedAt,
  }));
  const omitted = [
    { field: "EMA9 / EMA20 / weekly EMA / ATR / relative strength", why: "Not present on current cached payload; not calculated client-side" },
    { field: "Per-bar SMA20/SMA50/SMA200 series", why: "OHLCV bars do not carry SMA columns" },
    { field: "Per-trade RoR chart", why: "No per-trade outcome records on current GETs" },
    { field: "Invented strategy rows / strikes / weekly yield", why: "Would construct trades not supplied by the payload" },
    { field: "Company profile / executives / statements / peers", why: "Not in current cached output" },
    { field: "Earnings estimate/surprise/print-move history", why: "Not in current cached output" },
    { field: "SentiSense / viaNexus", why: "Out of scope for P1" },
    { field: "recommended_strategy as screen state", why: "Intentionally unused" },
  ];
  return { live, omitted, catalogue: WORKSPACE_FIELD_CATALOGUE };
}

function statusLabel(status) {
  if (status === FIELD_STATUS.observed) return LIVE_LABEL.observed;
  if (status === FIELD_STATUS.cached) return LIVE_LABEL.cached;
  if (status === FIELD_STATUS.derived) return "derived from cached data";
  if (status === FIELD_STATUS.partial) return "data unavailable";
  if (status === FIELD_STATUS.not_evaluated) return LIVE_LABEL.notEvaluated;
  return LIVE_LABEL.dataUnavailable;
}

export function liveDecision(ticker, expiryDate) {
  const exp = (ticker.availableExpirations || []).find((e) => e.date === expiryDate)
    || (ticker.availableExpirations || []).find((e) => e.date === ticker.defaultExpiryDate)
    || null;
  const dte = exp?.dte ?? null;
  const conflict = earningsConflict(ticker.earnings, dte);
  const basis = ticker.optionsState;
  const data = dataLayerOf(basis);
  const event = eventLayerOf(conflict);
  const action = ticker.basketState === "available"
    ? { key: "available", text: BASKET.available, tier: "info", mark: "●" }
    : { key: "unavailable", text: BASKET.unavailable, tier: "blocking", mark: "!" };
  const screen = screenFromLayers(data.key, event.key);
  return {
    conflict,
    dte,
    expiry: exp?.date || expiryDate || null,
    state: screen,
    headline: SCREEN[screen].headline,
    detail: reasonForScreen(data.key, event.key),
    layers: { data, event, action },
  };
}

export { EMPTY };
